import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { TitleStrategy, provideRouter, withComponentInputBinding } from '@angular/router';
import { StoreModule } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { LstAuthModule } from '@savvaslearning/lst-auth';

import { environment } from '../environments/environment';
import { DocumentTitleStrategy } from './core/services/document-metadata/document-title.strategy';
import { routes } from './app.routes';
import { LST_MODULE_OPTIONS } from './core/auth/auth.constants';
import { captureLaunchParams } from './core/auth/launch-params';
import { applyLegacyIdentity } from './core/auth/legacy-identity';
import { DocumentMetadataService } from './core/services/document-metadata/document-metadata.service';
import { applyLstIdentity } from './core/auth/lst-identity';
import { provideClassviewHighcharts } from './core/providers/highcharts.provider';
import { networkErrorInterceptor } from './core/services/connectivity/network-error.interceptor';
import { SessionStorageService } from './core/storage/session-storage.service';
import { deviceIdentityInterceptor } from './core/services/device-identity/device-identity.interceptor';

export const appConfig: ApplicationConfig = {
  providers: [
    // Stash the launch product-grade before lst-auth's SSO redirect drops the query params.
    provideAppInitializer(captureLaunchParams),
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideAnimationsAsync(),
    provideRouter(routes, withComponentInputBinding()),
    { provide: TitleStrategy, useClass: DocumentTitleStrategy },
    importProvidersFrom(StoreModule.forRoot({})),
    importProvidersFrom(TranslateModule.forRoot()),

    // LstAuthModule overrides HttpHandler, so provideHttpClient must come after it.
    // Reversed, every interceptor silently stops running.
    importProvidersFrom(LstAuthModule.forRoot(LST_MODULE_OPTIONS)),
    provideHttpClient(withInterceptors([networkErrorInterceptor, deviceIdentityInterceptor])),

    provideAppInitializer(() => {
      // TODO: seed the autosave interval (seconds) for both teacher and student,
      // regardless of role, until the login response carries it. Without this,
      // `resolveAutosaveInterval()` reads 0 on the non-legacy path and
      // rotating-lists autosave never fires.
      inject(SessionStorageService).set('IncrementalSaveDuration', '60');
      if (environment.useLegacyBackend) applyLegacyIdentity();
      else applyLstIdentity();
    }),
    // Owns the tab title and favicon; construct it at boot so they are right before
    // the first navigation, not only after it.
    provideAppInitializer(() => void inject(DocumentMetadataService)),

    provideClassviewHighcharts(),
  ],
};
